/**
 * AynaraHealthMonitor — Panel del Agente de Vigilancia
 * Muestra el estado de salud de todos los sistemas en tiempo real.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, ShieldAlert, ShieldOff, RefreshCw,
  CheckCircle2, AlertTriangle, XCircle, Activity, Clock,
  Zap, Wifi, CreditCard, MessageCircle, Video
} from 'lucide-react';
import { toast } from '../ui/sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-red-500/15', border: 'border-red-500/30', text: 'text-red-400', icon: XCircle,    badge: '🔴' },
  warning:  { bg: 'bg-amber-500/15', border: 'border-amber-500/30', text: 'text-amber-400', icon: AlertTriangle, badge: '🟡' },
  ok:       { bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle2, badge: '🟢' },
  info:     { bg: 'bg-blue-500/15', border: 'border-blue-500/30', text: 'text-blue-400', icon: Zap, badge: '🔵' },
};

const CHECK_LABELS = {
  meta_tokens:              { icon: Video,          label: 'Tokens Meta' },
  auto_publish_failures:    { icon: Activity,       label: 'Auto-publicación' },
  whatsapp_connection:      { icon: MessageCircle,  label: 'WhatsApp' },
  recent_publish_errors:    { icon: AlertTriangle,  label: 'Errores recientes' },
  stripe_webhook:           { icon: CreditCard,     label: 'Stripe / Intis' },
  content_calendar_staleness: { icon: Clock,        label: 'Calendario de contenido' },
};

function StatusBadge({ status }) {
  if (status === 'ok')       return <span className="flex items-center gap-1.5 text-emerald-400 text-sm font-semibold"><CheckCircle2 className="w-4 h-4" /> Todo en orden</span>;
  if (status === 'warning')  return <span className="flex items-center gap-1.5 text-amber-400 text-sm font-semibold"><AlertTriangle className="w-4 h-4" /> Avisos detectados</span>;
  if (status === 'critical') return <span className="flex items-center gap-1.5 text-red-400 text-sm font-semibold"><XCircle className="w-4 h-4" /> Crítico — acción requerida</span>;
  return <span className="text-text-muted text-sm">Escaneando…</span>;
}

export default function AynaraHealthMonitor({ authFetch }) {
  const [report, setReport]   = useState(null);
  const [history, setHistory] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/api/admin/health`);
      const j = await r.json();
      setReport(j.report || null);
      setHistory(j.history || []);
      if (j.report?.scanned_at) setLastScan(j.report.scanned_at);
    } catch { /* noop */ }
  }, [authFetch]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresco cada minuto
    return () => clearInterval(t);
  }, [load]);

  const forceScan = async () => {
    setScanning(true);
    try {
      const r = await authFetch(`${API}/api/admin/health/scan`, { method: 'POST' });
      const j = await r.json();
      setReport(j);
      if (j.scanned_at) setLastScan(j.scanned_at);
      toast.success('Escaneo completado');
    } catch { toast.error('Error al escanear'); }
    setScanning(false);
  };

  const issues = report?.issues || [];
  const critical = issues.filter(i => i.severity === 'critical');
  const warnings  = issues.filter(i => i.severity === 'warning');
  const infos     = issues.filter(i => i.severity === 'info');

  // Historial mini-chart
  const last24 = history.slice(0, 24).reverse();

  return (
    <div className="space-y-6" data-testid="aynara-health-monitor">

      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                report?.status === 'critical' ? 'bg-red-500/15' :
                report?.status === 'warning'  ? 'bg-amber-500/15' : 'bg-emerald-500/15'
              }`}>
                <ShieldCheck className={`w-5 h-5 ${
                  report?.status === 'critical' ? 'text-red-400' :
                  report?.status === 'warning'  ? 'text-amber-400' : 'text-emerald-400'
                }`} />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-text-heading">Aynara — Agente de Vigilancia</h2>
                <p className="text-xs text-text-muted">Monitoreo continuo · Escaneo cada 5 minutos</p>
              </div>
            </div>
            <div className="mt-2">
              <StatusBadge status={report?.status} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {lastScan && (
              <p className="text-xs text-text-muted">
                Último scan: {new Date(lastScan).toLocaleString('es', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
              </p>
            )}
            <button
              onClick={forceScan}
              disabled={scanning}
              className="btn-primary text-sm py-2.5 px-5 flex items-center gap-2"
              data-testid="health-force-scan-btn"
            >
              <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Escaneando…' : 'Escanear ahora'}
            </button>
          </div>
        </div>

        {/* Stats rápidas */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          {[
            { label: 'Críticos', value: critical.length, color: critical.length ? 'text-red-400' : 'text-text-muted' },
            { label: 'Avisos', value: warnings.length, color: warnings.length ? 'text-amber-400' : 'text-text-muted' },
            { label: 'Checks ok', value: (report?.checks?.length || 0) - (report?.checks?.filter(c => c.issues?.length > 0).length || 0), color: 'text-emerald-400' },
          ].map(s => (
            <div key={s.label} className="rounded-xl bg-white/[0.03] border border-border-subtle p-3 text-center">
              <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
              <p className="text-xs text-text-muted mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Mini historial visual */}
      {last24.length > 0 && (
        <div className="glass-card p-4">
          <p className="text-xs text-text-muted uppercase tracking-wider mb-3">Historial (últimas 24 lecturas)</p>
          <div className="flex items-end gap-1 h-8">
            {last24.map((h, i) => (
              <div
                key={i}
                title={`${h.ts?.slice(0, 16)} · ${h.status}`}
                className={`flex-1 rounded-sm transition-all ${
                  h.status === 'critical' ? 'bg-red-500 h-8' :
                  h.status === 'warning'  ? 'bg-amber-500 h-5' : 'bg-emerald-500 h-2'
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-text-muted mt-1">
            <span>Hace 24</span><span>Ahora</span>
          </div>
        </div>
      )}

      {/* Checks por módulo */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(report?.checks || []).map(check => {
          const meta = CHECK_LABELS[check.check] || { icon: Activity, label: check.check };
          const IconComp = meta.icon;
          const hasIssues = (check.issues || []).length > 0;
          const maxSeverity = (check.issues || []).find(i => i.severity === 'critical') ? 'critical' :
                              (check.issues || []).find(i => i.severity === 'warning')  ? 'warning'  : 'ok';
          const cfg = SEVERITY_CONFIG[hasIssues ? maxSeverity : 'ok'];

          return (
            <div key={check.check} className={`rounded-xl border p-4 ${cfg.bg} ${cfg.border}`} data-testid={`health-check-${check.check}`}>
              <div className="flex items-center gap-2 mb-2">
                <IconComp className={`w-4 h-4 ${cfg.text}`} />
                <p className={`text-sm font-semibold ${cfg.text}`}>{meta.label}</p>
              </div>
              {hasIssues ? (
                <ul className="space-y-1">
                  {(check.issues || []).map((issue, idx) => (
                    <li key={idx} className={`text-xs ${cfg.text} leading-relaxed`}>
                      {SEVERITY_CONFIG[issue.severity]?.badge} {issue.msg}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-emerald-400">Sin problemas detectados</p>
              )}
              {check.error && <p className="text-xs text-text-muted mt-1 italic">Error al ejecutar check: {check.error}</p>}
            </div>
          );
        })}

        {/* Placeholder si no hay datos aún */}
        {!report && (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border-subtle bg-white/[0.02] p-4 animate-pulse h-24" />
          ))
        )}
      </div>

      {/* Lista detallada de issues */}
      {issues.length > 0 && (
        <div className="glass-card p-5 space-y-3">
          <h3 className="text-sm font-heading font-semibold text-text-heading">
            Problemas activos ({issues.length})
          </h3>
          <div className="space-y-2">
            {[...critical, ...warnings, ...infos].map((issue, i) => {
              const cfg = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.info;
              const IssueIcon = cfg.icon;
              return (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${cfg.bg} ${cfg.border}`} data-testid={`health-issue-${i}`}>
                  <IssueIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cfg.text}`} />
                  <div className="min-w-0">
                    <p className={`text-xs font-medium ${cfg.text}`}>{issue.msg}</p>
                    {issue.owner && <p className="text-[10px] text-text-muted mt-0.5">Sistema: {issue.owner}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {issues.length === 0 && report && (
        <div className="glass-card p-8 text-center">
          <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-text-heading font-semibold">Todos los sistemas operativos</p>
          <p className="text-text-muted text-sm mt-1">Aynara no detectó ningún problema en el último escaneo.</p>
        </div>
      )}
    </div>
  );
}
